import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";

import { useApp } from "../context/AppContext";
import { SERVICE_LOCATION, LOGIN_API } from "../constants/endpoints";

import logo from "../assets/images/max-energy-logo.png";
import alertCircleIcon from "../assets/icons/alert-circle.svg";

const Login = () => {
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const { setLoading } = useApp();
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        if (!userName.trim() || !password.trim()) {
            setError("Please enter your username and password.");
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(
                SERVICE_LOCATION + LOGIN_API,
                { userName, password }
            );
            if (res.data.isSuccess) {
                navigate("/home/dashboard", { state: { data: res.data } });
            } else {
                setError(res.data.errorMessage || "Login failed. Please try again.");
            }
        } catch {
            setError("Unable to connect. Please check your network and try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────
    return (
        <div style={styles.bg}>
            {/* Decorative blobs */}
            <div style={styles.blob1} />
            <div style={styles.blob2} />

            <div style={styles.card} className="page-enter">

                {/* Logo + headings — centred */}
                <div style={styles.logoRow}>
                    <img src={logo} alt="MaxEnergy" style={styles.logo} />
                    <h1 style={styles.heading}>Welcome back</h1>
                    <p style={styles.sub}>Sign in to your client portal</p>
                </div>

                <form onSubmit={handleLogin} style={styles.form} noValidate>

                    {/* Error banner */}
                    {error && (
                        <div style={styles.errorBanner}>
                            <img src={alertCircleIcon} alt="" width={14} height={14} />
                            {error}
                        </div>
                    )}

                    {/* Username */}
                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="username">Username</label>
                        <InputText
                            id="username"
                            value={userName}
                            onChange={e => setUserName(e.target.value)}
                            placeholder="Enter your username"
                            autoComplete="username"
                            autoFocus
                            style={styles.input}
                        />
                    </div>

                    {/* Password */}
                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="password">
                            Password
                        </label>
                        <Password
                            inputId="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            feedback={false}
                            toggleMask
                            inputStyle={styles.input}
                            style={{ width: "100%" }}
                        />
                    </div>

                    {/* Sign In button */}
                    <Button
                        type="submit"
                        label="Sign In"
                        style={styles.submitBtn}
                    />

                </form>
            </div>
        </div>
    );
};

// ── Styles ────────────────────────────────────────────────────
const styles = {

    /* Full-page split background */
    bg: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1B5E20 0%, #2E7D32 45%, #F4F6F9 45%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
    },

    /* Decorative green blur circles */
    blob1: {
        position: "absolute",
        top: -80,
        right: -80,
        width: 320,
        height: 320,
        background: "rgba(76,175,80,0.12)",
        borderRadius: "50%",
        filter: "blur(60px)",
    },
    blob2: {
        position: "absolute",
        bottom: -60,
        left: -60,
        width: 260,
        height: 260,
        background: "rgba(255,255,255,0.06)",
        borderRadius: "50%",
        filter: "blur(40px)",
    },

    /* Login card */
    card: {
        background: "#ffffff",
        borderRadius: 20,
        boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
        padding: "44px 40px",
        width: "100%",
        maxWidth: 420,
        position: "relative",
        zIndex: 1,
    },

    /* Logo + title block */
    logoRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        marginBottom: 28,
    },
    logo: {
        maxWidth: 180,
        height: "auto",
    },
    heading: {
        fontFamily: "var(--font-sans)",
        fontSize: 26,
        fontWeight: 700,
        color: "#111827",
        margin: "12px 0 4px 0",
    },
    sub: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        color: "#6B7280",
        margin: 0,
    },

    /* Form layout */
    form: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
    },

    /* Each label + input pair */
    field: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },

    /* Field label */
    label: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 500,
        color: "#4B5563",
    },

    /* input */
    input: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 40,
        borderRadius: 6,
        border: "1px solid #D1D5DB",
        width: "100%",
        color: "#111827",
        padding: "0 12px",
    },

    /* Sign In button  */
    submitBtn: {
        fontFamily: "var(--font-sans)",
        width: "100%",
        justifyContent: "center",
        height: 44,
        fontSize: 14,
        fontWeight: 600,
        borderRadius: 8,
        background: "#4CAF50",
        borderColor: "#4CAF50",
        color: "#ffffff",
        marginTop: 4,
    },

    /* Error banner */
    errorBanner: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        background: "#FEE2E2",
        borderRadius: 8,
        color: "#991B1B",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 500,
        border: "1px solid #FECACA",
    },
};

export default Login;
